import React, { useEffect, useState } from 'react';
import apis from '../../utils/apis';
import httpAction from '../../utils/httpAction';
import { CardSkeleton, SkeletonText } from '../../components/common/Skeleton';
import { hasRole } from '../../utils/auth';
import Unauthorized from '../../pages/Unauthorized';

const inputStyle = { width:'100%', padding:12, border:'1px solid #d0d5dd', borderRadius:12 };
const roles = ['super_admin', 'editor', 'agent'];

export default function AdminUsers() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name:'', email:'', password:'', role:'editor' });

  const isAuthorized = hasRole(['super_admin']);

  const load = async () => {
    if (!isAuthorized) return;

    setLoading(true);
    const result = await httpAction({ url: apis().adminUsersList, silent: true });
    if (result?.status) setList(result.list || []);
    setLoading(false);
  };

  useEffect(() => {
    if (isAuthorized) load();
  }, [isAuthorized]);

  const submitHandler = async (e) => {
    e.preventDefault();
    setSaving(true);
    const result = await httpAction({ url: apis().adminUsersAdd, method:'POST', body: form });
    setSaving(false);
    if (result?.status) {
      setForm({ name:'', email:'', password:'', role:'editor' });
      load();
    }
  };

  const toggleStatus = async (item) => {
    const result = await httpAction({
      url: `${apis().adminUsersUpdate}/${item._id}`,
      method:'PUT',
      body:{ isActive: !item.isActive }
    });
    if (result?.status) load();
  };

  const changeRole = async (item, role) => {
    const result = await httpAction({
      url: `${apis().adminUsersUpdate}/${item._id}`,
      method:'PUT',
      body:{ role }
    });
    if (result?.status) load();
  };

  const deleteUser = async (item) => {
    if (!window.confirm(`Delete ${item.name}?`)) return;
    const result = await httpAction({
      url: `${apis().adminUsersDelete}/${item._id}`,
      method:'DELETE'
    });
    if (result?.status) load();
  };

  if (!isAuthorized) return <Unauthorized />;

  return (
    <div style={{ display:'grid', gap:24 }}>
      <div>
        <h2 style={{ margin:0 }}>Admin Users</h2>
        <p style={{ color:'#667085', marginTop:8 }}>Manage secure dashboard access with roles and account status.</p>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'380px 1fr', gap:24 }}>
        <form onSubmit={submitHandler} style={{ background:'#fff', padding:20, borderRadius:20, boxShadow:'0 12px 30px rgba(15,23,42,.06)' }}>
          <h3>Create Admin</h3>
          <div style={{ display:'grid', gap:12 }}>
            <input
              value={form.name}
              onChange={(e)=>setForm({ ...form, name:e.target.value })}
              placeholder='Full name'
              style={inputStyle}
              required
            />
            <input
              value={form.email}
              onChange={(e)=>setForm({ ...form, email:e.target.value })}
              type='email'
              placeholder='Email'
              style={inputStyle}
              required
            />
            <input
              value={form.password}
              onChange={(e)=>setForm({ ...form, password:e.target.value })}
              type='password'
              placeholder='Password'
              style={inputStyle}
              required
            />
            <select
              value={form.role}
              onChange={(e)=>setForm({ ...form, role:e.target.value })}
              style={inputStyle}
            >
              {roles.map((role)=><option key={role} value={role}>{role}</option>)}
            </select>
            <button
              disabled={saving}
              type='submit'
              style={{ padding:14, border:'none', borderRadius:12, background:'#0f5ef7', color:'#fff', fontWeight:700 }}
            >
              {saving ? 'Saving...' : 'Create Admin'}
            </button>
          </div>
        </form>

        <div style={{ background:'#fff', padding:20, borderRadius:20, boxShadow:'0 12px 30px rgba(15,23,42,.06)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <h3 style={{ margin:0 }}>Team Access</h3>
            <button onClick={load} style={{ border:'1px solid #d0d5dd', background:'#fff', padding:'10px 14px', borderRadius:10 }}>
              Refresh
            </button>
          </div>

          {loading ? (
            <>
              <SkeletonText width='180px' height={26} />
              <div style={{ marginTop:12 }}>
                <CardSkeleton count={3} height={110} />
              </div>
            </>
          ) : (
            <div style={{ display:'grid', gap:12 }}>
              {list.map((item) => (
                <div
                  key={item._id}
                  style={{
                    border:'1px solid #eef2f6',
                    borderRadius:16,
                    padding:16,
                    display:'grid',
                    gridTemplateColumns:'1fr auto',
                    gap:16
                  }}
                >
                  <div>
                    <div style={{ fontWeight:800 }}>{item.name}</div>
                    <div style={{ color:'#667085', marginTop:4 }}>{item.email}</div>
                    <div style={{ fontSize:13, color:'#667085', marginTop:6 }}>
                      Last login: {item.lastLoginAt ? new Date(item.lastLoginAt).toLocaleString() : 'Never'}
                    </div>
                  </div>

                  <div style={{ display:'grid', gap:8, minWidth:180 }}>
                    <select
                      value={item.role}
                      onChange={(e)=>changeRole(item, e.target.value)}
                      style={inputStyle}
                    >
                      {roles.map((role)=><option key={role} value={role}>{role}</option>)}
                    </select>

                    <button
                      onClick={()=>toggleStatus(item)}
                      style={{
                        border:'none',
                        borderRadius:10,
                        padding:10,
                        background:item.isActive ? '#e7f8ee' : '#fdecec',
                        color:item.isActive ? '#067647' : '#b42318',
                        fontWeight:700
                      }}
                    >
                      {item.isActive ? 'Active' : 'Disabled'}
                    </button>

                    <button
                      onClick={()=>deleteUser(item)}
                      style={{ border:'1px solid #f04438', color:'#f04438', borderRadius:10, padding:10, background:'#fff' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}

              {!list.length && <div style={{ color:'#667085' }}>No admin users found.</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}